import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  Download,
  ChevronDown,
  ChevronUp,
  Eye,
  Loader2,
  ExternalLink,
  MapPin,
  Phone,
  Mail,
  Calendar,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import operatorService from '../services/operatorService';
import * as XLSX from 'xlsx';

export default function OperatorsPage() {
  const [operators, setOperators] = useState([]);
  const [filteredOperators, setFilteredOperators] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'businessName', direction: 'asc' });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      setIsLoading(true);
      const data = await operatorService.getOperators();
      setOperators(data);
      setFilteredOperators(data);
    } catch (error) {
      toast.error(error || 'Failed to fetch operators');
      setOperators([]);
      setFilteredOperators([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let filtered = [...operators];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (op) =>
          op.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          op.dbaName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          op.licenseNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          op.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          op.county?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((op) => op.licenseStatus?.toLowerCase() === statusFilter.toLowerCase());
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    setFilteredOperators(filtered);
  }, [searchTerm, statusFilter, sortConfig, operators]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleExportExcel = () => {
    try {
      // Prepare data for export
      const exportData = filteredOperators.map((op) => ({
        'License Number': op.licenseNumber,
        'Business Name': op.businessName,
        'DBA Name': op.dbaName,
        'License Status': op.licenseStatus,
        'License Type': op.licenseType,
        'Phone': op.phone,
        'Email': op.email,
        'Address': `${op.streetAddress}, ${op.city}, ${op.state} ${op.zipCode}`,
        'County': op.county,
        'State': op.state,
        'License Designation': op.licenseDesignation,
        'Issue Date': op.issueDate ? new Date(op.issueDate).toLocaleDateString() : '',
        'Expiration Date': op.expirationDate ? new Date(op.expirationDate).toLocaleDateString() : '',
        'Contact 1': `${op.contactFirstName1 || ''} ${op.contactLastName1 || ''}`.trim(),
        'Contact 2': `${op.contactFirstName2 || ''} ${op.contactLastName2 || ''}`.trim(),
        'Website': op.website,
        'Facebook': op.facebook,
        'Instagram': op.instagram,
        'Twitter': op.twitter,
        'LinkedIn': op.linkedin,
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Operators');

      // Generate file name with current date
      const fileName = `operators_export_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);
      
      toast.success('Operators data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || '';
    let variant = 'secondary';
    
    if (statusLower === 'active') {
      variant = 'success';
    } else if (statusLower === 'pending') {
      variant = 'warning';
    } else if (statusLower === 'inactive' || statusLower === 'expired') {
      variant = 'destructive';
    }
    
    return (
      <Badge variant={variant} className="capitalize">
        {status}
      </Badge>
    );
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  const handleViewDetails = (operator) => {
    setSelectedOperator(operator);
    setIsDetailOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading operators...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>Operators</CardTitle>
                <CardDescription>
                  Manage and monitor all operators ({filteredOperators.length} results)
                </CardDescription>
              </div>
              <Button onClick={handleExportExcel}>
                <Download className="w-4 h-4 mr-2" />
                Export to Excel
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by business name, license, city, or county..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full md:w-48"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th
                      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition"
                      onClick={() => handleSort('licenseNumber')}
                    >
                      <div className="flex items-center">
                        License # <SortIcon columnKey="licenseNumber" />
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition"
                      onClick={() => handleSort('businessName')}
                    >
                      <div className="flex items-center">
                        Business Name <SortIcon columnKey="businessName" />
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition"
                      onClick={() => handleSort('licenseType')}
                    >
                      <div className="flex items-center">
                        License Type <SortIcon columnKey="licenseType" />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Status
                    </th>
                    <th
                      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition"
                      onClick={() => handleSort('city')}
                    >
                      <div className="flex items-center">
                        Location <SortIcon columnKey="city" />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Contact
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOperators.map((operator) => (
                    <tr
                      key={operator.operatorId}
                      className="border-b border-border hover:bg-muted/50 transition"
                    >
                      <td className="py-3 px-4">
                        <div className="font-medium text-foreground text-sm">
                          {operator.licenseNumber}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-foreground">{operator.businessName}</div>
                        {operator.dbaName && (
                          <div className="text-xs text-muted-foreground">DBA: {operator.dbaName}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {operator.licenseType}
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(operator.licenseStatus)}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        <div>{operator.city}, {operator.state}</div>
                        <div className="text-xs">{operator.county} County</div>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {operator.phone && <div className="text-muted-foreground">{operator.phone}</div>}
                        {operator.email && (
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {operator.email}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewDetails(operator)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredOperators.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No operators found matching your criteria.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Operator Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Operator Details</DialogTitle>
            <DialogDescription>
              Complete information for {selectedOperator?.businessName}
            </DialogDescription>
          </DialogHeader>

          {selectedOperator && (
            <div className="space-y-6 mt-4">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <User className="w-5 h-5 mr-2 text-primary" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/30 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">License Number</p>
                    <p className="font-medium">{selectedOperator.licenseNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">License Status</p>
                    <div className="mt-1">{getStatusBadge(selectedOperator.licenseStatus)}</div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Business Name</p>
                    <p className="font-medium">{selectedOperator.businessName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">DBA Name</p>
                    <p className="font-medium">{selectedOperator.dbaName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">License Type</p>
                    <p className="font-medium">{selectedOperator.licenseType}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">License Designation</p>
                    <p className="font-medium">{selectedOperator.licenseDesignation || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{selectedOperator.category || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">License Term</p>
                    <p className="font-medium">{selectedOperator.licenseTerm || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Phone className="w-5 h-5 mr-2 text-primary" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/30 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{selectedOperator.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedOperator.email || 'N/A'}</p>
                  </div>
                  {selectedOperator.contactFirstName1 && (
                    <div>
                      <p className="text-sm text-muted-foreground">Primary Contact</p>
                      <p className="font-medium">
                        {selectedOperator.contactFirstName1} {selectedOperator.contactLastName1}
                      </p>
                      {selectedOperator.contactType1 && (
                        <p className="text-xs text-muted-foreground">
                          ({selectedOperator.contactType1})
                        </p>
                      )}
                    </div>
                  )}
                  {selectedOperator.contactFirstName2 && (
                    <div>
                      <p className="text-sm text-muted-foreground">Secondary Contact</p>
                      <p className="font-medium">
                        {selectedOperator.contactFirstName2} {selectedOperator.contactLastName2}
                      </p>
                      {selectedOperator.contactType2 && (
                        <p className="text-xs text-muted-foreground">
                          ({selectedOperator.contactType2})
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Location Information */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-primary" />
                  Location
                </h3>
                <div className="bg-muted/30 p-4 rounded-lg space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Street Address</p>
                    <p className="font-medium">{selectedOperator.streetAddress}</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">City</p>
                      <p className="font-medium">{selectedOperator.city}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">State</p>
                      <p className="font-medium">{selectedOperator.state}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Zip Code</p>
                      <p className="font-medium">{selectedOperator.zipCode}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">County</p>
                      <p className="font-medium">{selectedOperator.county}</p>
                    </div>
                  </div>
                  {selectedOperator.latitude && selectedOperator.longitude && (
                    <div>
                      <p className="text-sm text-muted-foreground">Coordinates</p>
                      <p className="font-medium text-sm">
                        {selectedOperator.latitude}, {selectedOperator.longitude}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* License Dates */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-primary" />
                  License Dates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/30 p-4 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Issue Date</p>
                    <p className="font-medium">{formatDate(selectedOperator.issueDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Expiration Date</p>
                    <p className="font-medium">{formatDate(selectedOperator.expirationDate)}</p>
                  </div>
                </div>
              </div>

              {/* Social Media & Website */}
              {(selectedOperator.website || selectedOperator.facebook || selectedOperator.instagram || 
                selectedOperator.twitter || selectedOperator.linkedin) && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <ExternalLink className="w-5 h-5 mr-2 text-primary" />
                    Online Presence
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-muted/30 p-4 rounded-lg">
                    {selectedOperator.website && (
                      <a
                        href={selectedOperator.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Website
                      </a>
                    )}
                    {selectedOperator.facebook && (
                      <a
                        href={selectedOperator.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Facebook
                      </a>
                    )}
                    {selectedOperator.instagram && (
                      <a
                        href={selectedOperator.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Instagram
                      </a>
                    )}
                    {selectedOperator.twitter && (
                      <a
                        href={selectedOperator.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Twitter
                      </a>
                    )}
                    {selectedOperator.linkedin && (
                      <a
                        href={selectedOperator.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        LinkedIn
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedOperator.notes && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Notes</h3>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <p className="text-sm">{selectedOperator.notes}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
